import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Monitor, Smartphone, Laptop } from 'lucide-react';

const Compatibility: React.FC = () => {
  const { t } = useTranslation();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const platforms = [
    {
      icon: Monitor,
      title: t('compatibility.web'),
      description: 'Funciona perfeitamente em qualquer navegador moderno',
      color: 'primary',
    },
    {
      icon: Smartphone,
      title: t('compatibility.mobile'),
      description: 'Otimizado para dispositivos móveis e tablets',
      color: 'secondary',
    },
    {
      icon: Laptop,
      title: t('compatibility.windows'),
      description: 'Aplicação nativa para Windows com máxima performance',
      color: 'accent',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
      },
    },
  };

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            {t('compatibility.title')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('compatibility.subtitle')}
          </p>
        </motion.div>

        {/* Platforms */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="grid md:grid-cols-3 gap-8 mb-16"
        >
          {platforms.map((platform, index) => {
            const IconComponent = platform.icon;
            const colorClasses = {
              primary: 'bg-primary-100 text-primary-600',
              secondary: 'bg-secondary-100 text-secondary-600',
              accent: 'bg-accent-100 text-accent-600',
            };

            return (
              <motion.div
                key={index}
                variants={itemVariants}
                className="bg-white rounded-2xl p-8 text-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
              >
                <div className={`w-20 h-20 rounded-full ${colorClasses[platform.color as keyof typeof colorClasses]} flex items-center justify-center mb-6 mx-auto`}>
                  <IconComponent size={40} />
                </div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                  {platform.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {platform.description}
                </p>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Responsive Design */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="bg-gradient-to-r from-primary-600 to-secondary-600 rounded-2xl p-12 text-center text-white"
        >
          <h3 className="text-3xl font-bold mb-6">
            {t('compatibility.responsive')}
          </h3>
          <p className="text-xl opacity-90 mb-8 max-w-2xl mx-auto">
            Design adaptativo que se ajusta perfeitamente a qualquer tamanho de tela, desde smartphones até totens de grande porte.
          </p>
          
          {/* Device Mockups */}
          <div className="flex justify-center items-center space-x-8">
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 3, repeat: Infinity }}
              className="w-16 h-24 bg-white rounded-lg shadow-lg"
            />
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 3, repeat: Infinity, delay: 0.5 }}
              className="w-20 h-32 bg-white rounded-lg shadow-lg"
            />
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 3, repeat: Infinity, delay: 1 }}
              className="w-32 h-20 bg-white rounded-lg shadow-lg"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Compatibility; 